import { useEffect, useRef, useState } from "react";

export default function VideoShowcase() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="relative py-24 lg:py-32 bg-[var(--tt-green-900)] text-[var(--tt-cream)] overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-60" />
      <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-[var(--tt-orange)] opacity-10 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 w-[520px] h-[520px] rounded-full bg-[var(--tt-green-500)] opacity-20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left text */}
        <div className="lg:col-span-5 z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--tt-orange)]/40 text-[var(--tt-orange)] text-xs font-bold uppercase tracking-[0.18em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--tt-orange)] animate-pulse" />
            Tanıtım Videosu
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-5">
            İşimizi <span className="text-[var(--tt-orange)]">kendi gözlerinizle</span> görün.
          </h2>
          <p className="text-base sm:text-lg text-[var(--tt-cream)]/75 mt-6 max-w-md">
            Tuncay Ticaret atölyemizden, depomuzdan ve geri dönüşüm sahamızdan
            kareler. Kalite, düzen ve güven — hepsi tek bir karede.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="video-play-cta"
              onClick={toggle}
              className="inline-flex items-center gap-3 bg-[var(--tt-orange)] hover:bg-[var(--tt-orange-dark)] text-white font-bold rounded-full px-6 py-3 transition-colors"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
                {playing ? (
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                    <rect x="6" y="5" width="4" height="14" rx="1" />
                    <rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </span>
              {playing ? "Videoyu duraklat" : "Videoyu oynat"}
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { v: "HD", l: "Kalite" },
              { v: "720p", l: "Çözünürlük" },
              { v: "1:1", l: "Gerçek Saha" },
            ].map((s) => (
              <div
                key={s.l}
                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur"
              >
                <div className="font-display text-xl text-[var(--tt-orange)]">{s.v}</div>
                <div className="text-[11px] uppercase tracking-widest text-white/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right video */}
        <div className="lg:col-span-7">
          <div className="video-shell mx-auto max-w-[420px] sm:max-w-[460px]">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-[9/16]">
              <video
                ref={videoRef}
                data-testid="promo-video"
                src="/assets/tuncay-video.mp4"
                poster="/assets/tuncay-video-poster.jpg"
                playsInline
                loop
                muted={muted}
                preload="metadata"
                onClick={toggle}
                className="w-full h-full object-cover cursor-pointer"
              />
              {!playing && (
                <button
                  data-testid="video-overlay-play"
                  onClick={toggle}
                  aria-label="Videoyu oynat"
                  className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/55 via-black/10 to-transparent"
                >
                  <span className="flex items-center justify-center w-20 h-20 rounded-full bg-[var(--tt-orange)] text-white shadow-2xl hover:scale-110 transition-transform">
                    <svg viewBox="0 0 24 24" className="w-9 h-9 fill-current ml-1" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </button>
              )}

              <button
                data-testid="video-mute-toggle"
                onClick={toggleMute}
                aria-label={muted ? "Sesi aç" : "Sesi kapat"}
                className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur transition-colors"
              >
                {muted ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between mt-4 px-1 text-white/70 text-xs">
              <span className="font-semibold tracking-wide">TUNCAY TICARET • KILIS</span>
              <span className="uppercase tracking-[0.2em]">HD Tanıtım</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
